import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Search, Edit2, Archive, ArchiveRestore, Download,
  ChevronLeft, ChevronRight, Calendar, User, DollarSign, TrendingUp,
  Loader2, CheckCircle2
} from 'lucide-react';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Navbar';
import api from '../../services/api_client';
import './ContratsList.css';

const STATUTS_FILTER = [
  { code: 'Tous',               label: 'Tous' },
  { code: 'BROUILLON',          label: 'Brouillon' },
  { code: 'COMMUNIQUE_EN_COURS',label: 'Communiqué (en cours)' },
  { code: 'SIGNE',              label: 'Signé' },
  { code: 'ACTIF',              label: 'Actif' },
  { code: 'FIN_CDD',            label: 'Fin CDD' },
  { code: 'DEMISSION_CDI',      label: 'Démission (CDI)' },
  { code: 'PAS_DISCUTE',        label: 'Pas discuté' },
  { code: 'INACTIF',            label: 'Inactif (archivé)' },
];

const STATUT_LABELS = {
  'BROUILLON': 'Brouillon',
  'COMMUNIQUE_EN_COURS': 'Communiqué (en cours)',
  'SIGNE': 'Signé',
  'ACTIF': 'Actif',
  'FIN_CDD': 'Fin CDD',
  'DEMISSION_CDI': 'Démission (CDI)',
  'PAS_DISCUTE': 'Pas discuté',
  'INACTIF': 'Inactif (archivé)',
  // Rétrocompatibilité anciens statuts
  'Brouillon': 'Brouillon',
  'Actif': 'Actif',
  'Suspendu': 'Inactif (archivé)',
  'Terminé': 'Inactif (archivé)',
  'Expiré': 'Fin CDD',
};

function getStatutClass(statut) {
  const map = {
    'BROUILLON':           'statut-brouillon',
    'COMMUNIQUE_EN_COURS': 'statut-communique',
    'SIGNE':               'statut-signe',
    'ACTIF':               'statut-actif',
    'FIN_CDD':             'statut-fin-cdd',
    'DEMISSION_CDI':       'statut-demission',
    'PAS_DISCUTE':         'statut-pas-discute',
    'INACTIF':             'statut-inactif',
    // Rétrocompatibilité
    'Brouillon': 'statut-brouillon',
    'Actif':     'statut-actif',
    'Suspendu':  'statut-inactif',
    'Terminé':   'statut-inactif',
    'Expiré':    'statut-fin-cdd',
  };
  return map[statut] || 'statut-brouillon';
}

function getStatutLabel(statut) {
  return STATUT_LABELS[statut] || statut;
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatSalaire(s) {
  return s?.toLocaleString('fr-FR') + ' DT';
}

export default function ContratsList() {
  const navigate = useNavigate();
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('Tous');
  const [filterTypeContrat, setFilterTypeContrat] = useState('Tous');
  const [filterArchivage, setFilterArchivage] = useState('ACTIFS'); // 'ACTIFS', 'ARCHIVES', 'TOUS'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [downloadingId, setDownloadingId] = useState(null);

  // ============================================================
  // MODAL DE CONFIRMATION D'ARCHIVAGE
  // ============================================================
  const [archiveModal, setArchiveModal] = useState({
    open: false,
    id: null,
    ref: '',
  });

  const [archiveLoading, setArchiveLoading] = useState(false);
  const [restoreModal, setRestoreModal] = useState({
    open: false,
    id: null,
    ref: '',
  });

  const [restoreLoading, setRestoreLoading] = useState(false);

  const fetchContrats = async (arch = filterArchivage) => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/contrats', { params: { archivage: arch } });
      setContrats(res.data);
    } catch (err) {
      setError('Impossible de récupérer les contrats.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchContrats(filterArchivage); 
  }, [filterArchivage]);

  useEffect(() => { 
    setCurrentPage(1); 
  }, [searchTerm, filterStatut, filterTypeContrat, filterArchivage]);

  // ============================================================
  // OUVRIR LA MODAL D'ARCHIVAGE
  // ============================================================
  const openArchiveModal = (id, ref) => {
    setError('');

    setArchiveModal({
      open: true,
      id,
      ref: ref || '',
    });
  };

  // ============================================================
  // FERMER LA MODAL D'ARCHIVAGE
  // ============================================================
  const closeArchiveModal = () => {
    if (archiveLoading) return;

    setArchiveModal({
      open: false,
      id: null,
      ref: '',
    });
  };

  // ============================================================
  // ARCHIVER LE CONTRAT
  // ============================================================
  const handleArchive = async () => {
    if (!archiveModal.id || archiveLoading) return;

    setArchiveLoading(true);
    setError('');

    try {
      await api.post(
        `/contrats/${archiveModal.id}/archive`
      );

      const ref = archiveModal.ref;

      setSuccessMsg(
        `Le contrat ${ref} a été archivé avec succès.`
      );

      // Fermer immédiatement la modal après succès
      setArchiveModal({
        open: false,
        id: null,
        ref: '',
      });

      // Rafraîchir la liste
      await fetchContrats(filterArchivage);

      // Effacer le message après 4 secondes
      setTimeout(() => {
        setSuccessMsg('');
      }, 4000);

    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        "Erreur lors de l'archivage du contrat."
      );
    } finally {
      setArchiveLoading(false);
    }
  };


const openRestoreModal = (id, ref) => {
  setError('');
  setRestoreModal({ open: true, id, ref: ref || '' });
};

const closeRestoreModal = () => {
  if (restoreLoading) return;
  setRestoreModal({ open: false, id: null, ref: '' });
};   
  const handleRestore = async () => {
  if (!restoreModal.id || restoreLoading) return;

  setRestoreLoading(true);
  setError('');

  try {
    await api.post(`/contrats/${restoreModal.id}/restaurer`);

    const ref = restoreModal.ref;

    setRestoreModal({ open: false, id: null, ref: '' });
    setSuccessMsg(`Le contrat ${ref} a été restauré avec succès.`);

    await fetchContrats(filterArchivage);

    setTimeout(() => setSuccessMsg(''), 4000);
  } catch (err) {
    setError(
      err?.response?.data?.detail ||
      'Erreur lors de la restauration du contrat.'
    );
  } finally {
    setRestoreLoading(false);
  }
};

  // Filtrage côté client
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = contrats.filter(c => {
    const fullText = `${c.reference} ${c.employe?.nom || ''} ${c.employe?.prenom || ''} ${c.employe?.matricule || ''}`.toLowerCase();
    const matchSearch = fullText.includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === 'Tous' || c.statut === filterStatut || (filterStatut === 'INACTIF' && ['Suspendu', 'Terminé'].includes(c.statut));
    const matchType = filterTypeContrat === 'Tous' || c.type_contrat === filterTypeContrat;
    return matchSearch && matchStatut && matchType;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


  const handleGenererWord = async (id, ref) => {
    setDownloadingId(id);
    try {
      const res = await api.post(`/contrats/${id}/generer-word`, null, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contrat_${ref}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erreur lors de la génération du document Word.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Stats
  const totalActifs   = contrats.filter(c => ['ACTIF', 'Actif'].includes(c.statut)).length;
  const totalBrouillons = contrats.filter(c => ['BROUILLON', 'Brouillon'].includes(c.statut)).length;
  const totalExpires  = contrats.filter(c =>
    c.date_fin && new Date(c.date_fin) < today && !['INACTIF', 'Inactif', 'FIN_CDD', 'DEMISSION_CDI', 'Terminé', 'Expiré'].includes(c.statut)
  ).length;

  // Pagination
  const renderPages = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages.map((p, i) => {
      if (p === '...') return <span key={`e-${i}`} className="pagination-ellipsis">...</span>;
      return (
        <button key={p} onClick={() => setCurrentPage(p)}
          className={`pagination-circle-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>
      );
    });
  };

  return (
    <div className="app-container">
     
      <main className="main-content">
        
        <div className="content-padding">

          {/* Stats */}
          <div className="stats-grid contrats-stats">
            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper blue">
                <FileText size={22} className="stat-icon" />
              </div>
              <div className="stat-details">
                <span className="stat-label">Total contrats</span>
                <span className="stat-value">{contrats.length}</span>
                <span className="stat-subtitle">Tous statuts confondus</span>
              </div>
            </div>
            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper green">
                <TrendingUp size={22} className="stat-icon" />
              </div>
              <div className="stat-details">
                <span className="stat-label">Actifs</span>
                <span className="stat-value">{totalActifs}</span>
                <span className="stat-subtitle">Contrats en cours</span>
              </div>
            </div>
            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper purple">
                <DollarSign size={22} className="stat-icon" />
              </div>
              <div className="stat-details">
                <span className="stat-label">Brouillons</span>
                <span className="stat-value">{totalBrouillons}</span>
                <span className="stat-subtitle">En attente de finalisation</span>
              </div>
            </div>
            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper orange">
                <Calendar size={22} className="stat-icon" />
              </div>
              <div className="stat-details">
                <span className="stat-label">Expirés</span>
                <span className="stat-value">{totalExpires}</span>
                <span className="stat-subtitle">Date de fin dépassée</span>
              </div>
            </div>
          </div>

          {/* Table card */}
          <div className="table-card glass-card">
            {/* Filters */}
            <div className="contrats-filters-bar">
              <div className="contrats-filter-group">
                <span className="contrats-filter-label">Recherche</span>
                <div className="contrats-search-wrapper">
                  <Search size={15} className="contrats-search-icon" />
                  <input
                    type="text"
                    placeholder="Référence, employé..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="contrats-search-input"
                  />
                </div>
              </div>
              <div className="contrats-filter-group">
                <span className="contrats-filter-label">Statut</span>
                <select
                  value={filterStatut}
                  onChange={e => setFilterStatut(e.target.value)}
                  className="contrats-filter-select"
                >
                  {STATUTS_FILTER.map(s => (
                    <option key={s.code} value={s.code}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="contrats-filter-group">
                <span className="contrats-filter-label">Type</span>
                <select
                  value={filterTypeContrat}
                  onChange={e => setFilterTypeContrat(e.target.value)}
                  className="contrats-filter-select"
                >
                  <option value="Tous">Tous</option>
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="STAGE">STAGE</option>
                  <option value="ALTERNANCE">ALTERNANCE</option>
                  <option value="CIVP">CIVP</option>
                </select>
              </div>
              {/*<div className="contrats-filter-group">
                <span className="contrats-filter-label">Affichage</span>
                <select
                  value={filterArchivage}
                  onChange={e => { setFilterArchivage(e.target.value); setCurrentPage(1); }}
                  className="contrats-filter-select"
                >
                  <option value="ACTIFS">Actifs</option>
                  <option value="ARCHIVES">Archivés</option>
                  <option value="TOUS">Tous</option>
                </select>
              </div>
              <div className="contrats-filter-actions">
                <button
                  className="contrats-add-btn"
               onClick={() => navigate('/rh/contracts/new')}
                >
                  <Plus size={18} />
                  Nouveau contrat
                </button>
              </div>*/}
            </div>

            {successMsg && (
              <div
                className="alert-success-banner glass-card"
                style={{ marginBottom: '16px' }}
              >
                <CheckCircle2
                  size={18}
                  className="success-banner-icon"
                />

                <span>{successMsg}</span>

                <button
                  type="button"
                  onClick={() => setSuccessMsg('')}
                  aria-label="Fermer le message"
                  className="success-banner-close"
                >
                  ×
                </button>
              </div>
            )}

            {/* ========================================================
                MODAL DE CONFIRMATION D'ARCHIVAGE
                ======================================================== */}
            {archiveModal.open && (
              <div
                className="archive-modal-overlay"
                onClick={closeArchiveModal}
              >
                <div
                  className="archive-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="archive-modal-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="archive-modal-icon">
                    <Archive size={26} />
                  </div>

                  <div className="archive-modal-content">
                    <h3 id="archive-modal-title">
                      Archiver le contrat ?
                    </h3>

                    <p className="archive-modal-question">
                      Voulez-vous archiver le contrat
                      <strong>
                        {archiveModal.ref
                          ? ` ${archiveModal.ref}`
                          : ''}
                      </strong>
                      ?
                    </p>

                    <p className="archive-modal-description">
                      Les données seront conservées dans le système.
                      Le contrat ne sera plus considéré comme actif
                      et sera accessible dans la section des contrats
                      archivés.
                    </p>
                  </div>

                  <div className="archive-modal-actions">
                    <button
                      type="button"
                      className="archive-modal-btn archive-modal-btn-cancel"
                      onClick={closeArchiveModal}
                      disabled={archiveLoading}
                    >
                      Annuler
                    </button>

                    <button
                      type="button"
                      className="archive-modal-btn archive-modal-btn-confirm"
                      onClick={handleArchive}
                      disabled={archiveLoading}
                    >
                      {archiveLoading ? (
                        <>
                          <Loader2
                            size={15}
                            className="archive-spin"
                          />
                          Archivage...
                        </>
                      ) : (
                        <>
                          <Archive size={15} />
                          Archiver
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            
           
              {/* MODAL DE CONFIRMATION DE RESTAURATION */}
              {restoreModal.open && (
                <div
                  className="restore-modal-overlay"
                  onClick={closeRestoreModal}
                >
                  <div
                    className="restore-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="restore-modal-title"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="restore-modal-icon">
                      <ArchiveRestore size={27} />
                    </div>

                    <div className="restore-modal-content">
                      <span className="restore-modal-eyebrow">
                        GESTION DES CONTRATS
                      </span>

                      <h3 id="restore-modal-title">
                        Restaurer ce contrat ?
                      </h3>

                      <p className="restore-modal-question">
                        Vous êtes sur le point de restaurer
                        <strong> {restoreModal.ref}</strong>.
                      </p>

                      <div className="restore-modal-info">
                        <CheckCircle2 size={19} />
                        <p>
                          Le contrat sera réintégré dans la liste des contrats
                          actifs ou non archivés. Son statut dépendra de sa
                          période de validité.
                        </p>
                      </div>
                    </div>

                    <div className="restore-modal-actions">
                      <button
                        type="button"
                        className="restore-modal-btn restore-modal-btn-cancel"
                        onClick={closeRestoreModal}
                        disabled={restoreLoading}
                      >
                        Annuler
                      </button>

                      <button
                        type="button"
                        className="restore-modal-btn restore-modal-btn-confirm"
                        onClick={handleRestore}
                        disabled={restoreLoading}
                      >
                        {restoreLoading ? (
                          <>
                            <Loader2 size={16} className="archive-spin" />
                            Restauration...
                          </>
                        ) : (
                          <>
                            <ArchiveRestore size={16} />
                            Restaurer le contrat
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}



            {/* Table */}
            {loading ? (
              <div className="table-message">Chargement...</div>
            ) : error ? (
              <div className="table-message error">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="table-message">Aucun contrat trouvé.</div>
            ) : (
              <div className="table-scroll-container">
                <table className="custom-premium-table">
                  <thead>
                    <tr>
                      <th>Référence</th>
                      <th>Employé</th>
                      <th>Type</th>
                      <th>Date début</th>
                      <th>Date fin</th>
                      <th>Salaire mensuel</th>
                      <th>Statut</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(c => (
                      <tr key={c.id}>
                        <td>
                          <span className="contrat-ref-badge">{c.reference}</span>
                        </td>
                  
                        
                        <td>
                          {(() => {
                            // Priorité à l'employé, sinon afficher le candidat
                            const personne = c.employe || c.candidate || c.candidat;
                            const estCandidat = !c.employe && !!personne;

                            const prenom =
                              personne?.first_name ||
                              personne?.prenom ||
                              "";

                            const nom =
                              personne?.last_name ||
                              personne?.nom ||
                              "";

                            const email = personne?.email || "";

                            const matricule =
                              personne?.matricule ||
                              personne?.employee_number ||
                              "";

                            const initials =
                              `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();

                            if (!personne) {
                              return (
                                <span className="contrat-indefini">
                                  {c.candidate_id || c.candidat_id
                                    ? `Candidat #${c.candidate_id || c.candidat_id}`
                                    : c.employee_id
                                      ? `Employé #${c.employee_id}`
                                      : "Personne non associée"}
                                </span>
                              );
                            }

                            return (
                              <div className="contrat-employe-cell">
                                <div className="contrat-employe-avatar">
                                  {initials || "?"}
                                </div>

                                <div className="contrat-employe-info">
                                  <span className="contrat-employe-nom">
                                    {[prenom, nom].filter(Boolean).join(" ") ||
                                      "Nom non renseigné"}
                                  </span>

                                  <span className="contrat-employe-matricule">
                                    {estCandidat
                                      ? email || `Candidat #${c.candidate_id || c.candidat_id || personne.id}`
                                      : matricule || email}
                                  </span>

                                  {estCandidat && (
                                    <span className="contrat-indefini">
                                      Candidat — en attente de signature
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </td>


                        <td>
                          <span className={`contrat-type-badge ${c.type_contrat?.toLowerCase() || ''}`}>
                            {c.type_contrat}
                          </span>
                        </td>
                        <td className="contrat-date-cell">
                          <div className="contrat-date-wrapper">
                            <Calendar size={13} className="contrat-date-icon" />
                            {formatDate(c.date_debut)}
                          </div>
                        </td>
                        <td className="contrat-date-cell">
                          {c.date_fin ? (
                            <div className="contrat-date-wrapper">
                              <Calendar size={13} className="contrat-date-icon" />
                              {formatDate(c.date_fin)}
                            </div>
                          ) : (
                            <span className="contrat-indefini">Indéfini</span>
                          )}
                        </td>
                        <td className="contrat-salaire-cell">
                          {formatSalaire(c.salaire_mensuel)}
                        </td>
                        <td>
                          <span className={`contrat-statut-badge ${getStatutClass(c.statut)}`}>
                            {getStatutLabel(c.statut)}
                          </span>
                        </td>
                        <td>
                          <div className="actions-buttons-container">
                            <button
                              onClick={() => handleGenererWord(c.id, c.reference)}
                              className="action-circle-btn-word"
                              title="Générer Word"
                              disabled={downloadingId === c.id}
                            >
                              {downloadingId === c.id ? <Loader2 size={14} className="cl-spin" /> : <Download size={14} />}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/rh/contrats/modifier/${c.id}`
                                )
                              }
                              className="action-circle-btn-edit"
                              title="Modifier ce contrat"
                            >
                              <Edit2 size={14} />
                            </button>
                            {['INACTIF', 'Inactif', 'Suspendu', 'Terminé'].includes(c.statut) ? (
                              <button
                                onClick={() => openRestoreModal(c.id, c.reference)}
                                className="action-circle-btn-restore"
                                title="Restaurer ce contrat"
                              >
                                <ArchiveRestore size={14} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  openArchiveModal(
                                    c.id,
                                    c.reference
                                  )
                                }
                                className="action-circle-btn-archive"
                                title="Archiver ce contrat"
                              >
                                <Archive size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="pagination-bar-premium">
            <span className="pagination-info-premium">
              {filtered.length} contrat(s) - page {currentPage} / {totalPages}
            </span>
            <div className="pagination-controls-premium">
              <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}
                className="pagination-circle-btn arrow-btn"><ChevronLeft size={16} /></button>
              <div className="pagination-numbers-container">{renderPages()}</div>
              <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                className="pagination-circle-btn arrow-btn"><ChevronRight size={16} /></button>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}



